import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { LogOut, User, RefreshCw, Lock } from 'lucide-react';
import EmployeeCard from './EmployeeCard';
import PasswordChangeForm from './PasswordChangeForm';
import axios from 'axios';

const EmployeeDashboard = ({ authHeader, onLogout }) => {
  const [employee, setEmployee] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const { toast } = useToast();

  // Fetch employee data based on authentication
  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const config = {
        headers: { 'Authorization': authHeader }
      };
      
      // Get current user data from the new endpoint
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const apiUrl = BACKEND_URL ? `${BACKEND_URL}/api/employees/me` : '/api/employees/me';
      const response = await axios.get(apiUrl, config);
      setEmployee(response.data);
    } catch (err) {
      console.error('Error fetching employee data:', err);
      
      // If authentication error, logout
      if (err.response && err.response.status === 401) {
        onLogout();
        toast({
          title: "Сесія закінчилася",
          description: "Будь ласка, увійдіть знову",
          variant: "destructive",
        });
      } else {
        setError('Помилка завантаження даних');
        toast({
          title: "Помилка",
          description: "Не вдалося завантажити ваші дані",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch summary data to get the period
  const fetchSummaryData = async () => {
    try {
      const config = {
        headers: { 'Authorization': authHeader }
      };

      // Get summary data from the endpoint
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const apiUrl = BACKEND_URL ? `${BACKEND_URL}/api/employees/summary` : '/api/employees/summary';
      const response = await axios.get(apiUrl, config);
      setSummary(response.data);
    } catch (err) {
      console.error('Error fetching summary data:', err);
    }
  };

  useEffect(() => {
    fetchEmployeeData();
    fetchSummaryData();
  }, [authHeader]);

  const handleRefreshData = () => {
    fetchEmployeeData();
  };

  const handlePasswordChanged = () => {
    setShowPasswordForm(false);
    toast({
      title: "Успіх",
      description: "Пароль успішно змінено",
      variant: "default",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Завантаження даних...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md border-red-200">
          <CardContent className="py-12 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-red-900 mb-2">Помилка завантаження</h3>
            <p className="text-red-600 mb-4">{error}</p>
            <div className="flex space-x-2 justify-center">
              <Button onClick={handleRefreshData} variant="outline">
                Спробувати знову
              </Button>
              <Button onClick={onLogout} variant="default" className="flex items-center space-x-2">
                <LogOut className="w-4 h-4" />
                <span>Вийти</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Особистий кабінет</h1>
              <p className="text-sm text-gray-600">Ваша інформація про зарплату та показники роботи</p>
              {summary && summary.period && (
                <p className="text-sm text-gray-600">{summary.period} року</p>
              )}
            </div>
            <div className="flex flex-col space-y-2">
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRefreshData}
                  disabled={loading}
                  className="flex items-center space-x-2"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  {/* <span>Оновити</span> */}
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={onLogout}
                  className="flex items-center space-x-2"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Вийти</span>
                </Button>
              </div>
              <span></span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPasswordForm(!showPasswordForm)}
                className="flex items-center space-x-2"
              >
                <Lock className="w-4 h-4" />
                <span>{showPasswordForm ? 'Сховати' : 'Змінити пароль'}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {showPasswordForm && (
          <div className="mb-6">
            <PasswordChangeForm 
              authHeader={authHeader} 
              onPasswordChanged={handlePasswordChanged} 
            />
          </div>
        )}
        
        {employee && (
          <EmployeeCard employee={employee} />
        )}
        
        {!employee && !loading && (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-6 h-6 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Дані не знайдено</h3>
              <p className="text-gray-600 mb-4">
                Не вдалося отримати вашу інформацію
              </p>
              <div className="flex space-x-2 justify-center">
                <Button onClick={handleRefreshData} variant="outline">
                  Спробувати знову
                </Button>
                <Button onClick={onLogout} variant="default" className="flex items-center space-x-2">
                  <LogOut className="w-4 h-4" />
                  <span>Вийти</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-gray-600">
          <p>© 2025 Система управління персоналом. Звіт згенеровано автоматично.</p>
        </div>
      </footer>
    </div>
  );
};

export default EmployeeDashboard;